import React, { useState, useEffect } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';

const AddForYouCategory = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [video, setVideo] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categoriesOptions, setCategoriesOptions] = useState([]);
  const [showLoadingModal, setShowLoadingModal] = useState(false);

  // Fetch ForYouCategories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/for-you-category/foryou-category`);
  
        // Map into format for react-select
        const options = response.data.data.map(category => ({
          value: category.id,
          label: category.name
        }));
  
        setCategoriesOptions(options);
      } catch (error) {
        toast.error('Failed to load categories');
      }
    };
  
    fetchCategories();
  }, []);
  
  const handleThumbnailUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setThumbnail(file);
      // Create preview URL for the image
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    if (!title.trim()) {
      toast.error('Title is required.');
      return false;
    }
    if (!video) {
      toast.error('Video is required.');
      return false;
    }
    if (!thumbnail) {
      toast.error('Thumbnail is required.');
      return false;
    }
    if (selectedCategories.length === 0) {
      toast.error('At least one category is required.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;
    
    const formData = new FormData();
    formData.append('title', title);
    formData.append('video', video);
    formData.append('thumbnail', thumbnail);
    formData.append('categories', JSON.stringify(selectedCategories.map(cat => cat.value)));

    setIsLoading(true);
    setShowLoadingModal(true); // Show the loading modal

    try {      
      const request = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/for-you-category/create-foryou-video`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          // Configuration for large file uploads (200MB)
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
          timeout: 300000, // 5 minutes timeout for initial upload
        }
      );
      
      setShowLoadingModal(false); // Hide the loading modal
      setIsLoading(false);
      
      if (request.data.message) {
        toast.success('Video category created successfully!');
        setTimeout(() => navigate('/for-you-video'), 2000);
      } else {
        toast.error('Could not create video category');
      }
    } catch (error) {
      setShowLoadingModal(false); // Hide the loading modal
      setIsLoading(false);
      toast.error(error.response?.data?.message || 'An error occurred');
    }
  };

  // Loading Modal Component
  const LoadingModal = () => {
    return (
      <Modal
        show={showLoadingModal}
        centered
        backdrop="static"
        keyboard={false}
        size="sm"
      >
        <Modal.Body style={loadingModalStyle}>
          <Spinner animation="border" role="status" style={spinnerStyle} />
          <p style={loadingTextStyle}>Compressing video...</p>
        </Modal.Body>
      </Modal>
    );
  };

  return (
    <div style={containerStyle}>
      <ToastContainer />
      <LoadingModal />
      <div className="shadow rounded" style={cardStyle}>
        <div style={sectionTitleStyle}>
          <h3 style={titleTextStyle}>Add Video Category</h3>
        </div>
        <div style={userInfoContainer}>
          {/* Title Field */}
          <div style={profileFieldStyle}>
            <label style={profileLabelStyle}>Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              style={profileInputStyle}
              placeholder="Enter video title"
              maxLength={50}
              required
            />
          </div>

          {/* Video Upload Field */}
          <div style={profileFieldStyle}>
            <label style={profileLabelStyle}>Upload Video</label>
            <input
              type="file"
              accept="video/*"
              onChange={(e) => setVideo(e.target.files[0])}
              style={profileInputStyle}
              required
            />
          </div>

          {/* Thumbnail Upload Field */}
          <div style={profileFieldStyle}>
            <label style={profileLabelStyle}>Thumbnail Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleThumbnailUpload}
              style={profileInputStyle}
              required
            />
            {thumbnailPreview && (
              <img 
                src={thumbnailPreview} 
                alt="Thumbnail Preview" 
                style={thumbnailPreviewStyle} 
              />
            )}
          </div>

          {/* Multi-Select Category Dropdown */}
          <div style={profileFieldStyle}>
            <label style={profileLabelStyle}>Categories</label>
            <Select
              isMulti
              options={categoriesOptions}
              value={selectedCategories}
              onChange={setSelectedCategories}
              placeholder="Select categories"
            />
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="add-btn-align">
        <Button
          type="submit"
          style={buttonStyle}
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {isLoading ? 'Add' : 'Add'}
        </Button>
      </div>
    </div>
  );
};

// Inline Styles
const containerStyle = {
  maxWidth: '90%',
  fontFamily: 'Poppins',
  fontWeight: 600,
};

const cardStyle = {
  backgroundColor: 'white',
  borderRadius: '8px',
  marginBottom: '20px',
  border: 'none',
  width: '50%',
};

const sectionTitleStyle = {
  color: 'white',
  backgroundColor: '#064260',
  padding: '10px',
  borderRadius: '8px 8px 0 0',
  textAlign: 'left',
  fontWeight: 600,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const titleTextStyle = {
  fontSize: '21px',
  margin: 0,
  marginLeft: "30px",
};

const buttonStyle = {
  backgroundColor: '#27B99A',
  color: 'white',
  border: 'none',
  borderRadius: '20px',
  fontSize: '16px',
  cursor: 'pointer',
  width: '15%',
  marginRight: '30px',
};

const userInfoContainer = {
  display: 'flex',
  flexDirection: 'column',
  padding: '20px 40px',
  gap: '20px',
};

const profileFieldStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const profileLabelStyle = {
  fontWeight: 'bold',
  fontSize: '14px',
};

const profileInputStyle = {
  padding: '8px',
  borderRadius: '4px',
  backgroundColor: '#F4F4F6',
  border: 'none',
  width: '100%',
};

// Thumbnail preview style
const thumbnailPreviewStyle = {
  marginTop: '10px',
  maxWidth: '100%',
  maxHeight: '200px',
  borderRadius: '4px',
};

// Loading modal styles
const loadingModalStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '30px 20px',
};

const spinnerStyle = {
  color: '#27B99A',
  width: '3rem',
  height: '3rem',
  marginBottom: '15px',
};

const loadingTextStyle = {
  fontSize: '16px',
  fontWeight: '500',
  textAlign: 'center',
  margin: 0,
};

export default AddForYouCategory;